<script setup lang="ts">
/**
 * /actions — target first: pick where (a lineage's environment), then see
 * what you can run there, with the last run of each action on that target.
 * "All actions" lists every action with the targets it is enabled on.
 * Deep links: /actions?target=strauss-eu:staging, /actions?view=all&q=…
 */
import { History, Layers, ListChecks, Play, SearchX, Server, Zap } from "@lucide/vue";
import { computed, ref, watch } from "vue";
import type { Runbook } from "@/api/contracts";
import { groupTargets, isProd, parseTarget, useLatestRuns, useRunbooks } from "@/api/runbooks";
import ActionCard from "@/components/actions/ActionCard.vue";
import RunActionForm from "@/components/actions/RunActionForm.vue";
import TargetChip from "@/components/actions/TargetChip.vue";
import EmptyState from "@/components/common/EmptyState.vue";
import ErrorState from "@/components/common/ErrorState.vue";
import FilterBar from "@/components/common/FilterBar.vue";
import ListSkeleton from "@/components/common/ListSkeleton.vue";
import PageHeader from "@/components/common/PageHeader.vue";
import PageTabs from "@/components/common/PageTabs.vue";
import SearchInput from "@/components/common/SearchInput.vue";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useRouteQuery } from "@/composables/useRouteQuery";
import { t } from "@/copy/strings";
import { cn } from "@/lib/utils";

definePage({ meta: { title: "Actions" } });

const runbooks = useRunbooks();
const view = useRouteQuery("view", "target");
const target = useRouteQuery("target");
const q = useRouteQuery("q");
const targetQ = ref("");
const VIEWS = [
  { id: "target", label: t.actions.byTarget, icon: Server },
  { id: "all", label: t.actions.all, icon: Layers },
];

const actions = computed(() => runbooks.data.value ?? []);
const allTargets = computed(() => [...new Set(actions.value.flatMap((a) => a.enabledTargets))]);
const groups = computed(() => {
  const needle = targetQ.value.trim().toLowerCase();
  return groupTargets(allTargets.value.filter((id) => !needle || id.toLowerCase().includes(needle)));
});

// Remember the last target (per browser) so returning people land where they left.
const LAST_KEY = "smake.actions.target";
watch(
  [allTargets, target, view],
  () => {
    if (view.value !== "target" || !allTargets.value.length) return;
    if (target.value && allTargets.value.includes(target.value)) {
      try {
        localStorage.setItem(LAST_KEY, target.value);
      } catch {
        /* private mode */
      }
      return;
    }
    if (target.value) return; // unknown target in the link: show it as "nothing here"
    let last: string | null = null;
    try {
      last = localStorage.getItem(LAST_KEY);
    } catch {
      /* ignore */
    }
    const fallback = last && allTargets.value.includes(last) ? last : allTargets.value.length === 1 ? allTargets.value[0] : "";
    if (fallback) target.value = fallback;
  },
  { immediate: true },
);

const matches = (a: Runbook) => {
  const needle = q.value.trim().toLowerCase();
  return !needle || [a.name, a.title, a.description, ...a.steps.map((s) => s.title ?? s.summary)].some((s) => s.toLowerCase().includes(needle));
};
const onTarget = computed(() => actions.value.filter((a) => a.enabledTargets.includes(target.value) && matches(a)));
const filteredAll = computed(() => actions.value.filter(matches));

const latest = useLatestRuns(() => ({ target: target.value }), () => view.value === "target" && Boolean(target.value));
const lastRunOf = (name: string) => latest.data.value?.find((r) => r.runbook === name) ?? null;

const selected = computed(() => (target.value ? parseTarget(target.value) : null));

// Run sheet
const sheetAction = ref<Runbook | null>(null);
const sheetOpen = ref(false);
function openRun(a: Runbook) {
  sheetAction.value = a;
  sheetOpen.value = true;
}
</script>

<template>
  <div>
    <PageHeader :title="t.actions.title" :description="t.actions.description">
      <template #actions>
        <Button as-child variant="outline"><RouterLink to="/actions/runs"><History /> {{ t.actions.history }}</RouterLink></Button>
      </template>
    </PageHeader>

    <ListSkeleton v-if="runbooks.isPending.value" :rows="4" />
    <ErrorState v-else-if="runbooks.error.value" :error="runbooks.error.value" @retry="runbooks.refetch()" />
    <EmptyState v-else-if="actions.length === 0" :icon="ListChecks" :title="t.actions.noTargets" :description="t.actions.noTargetsHint" />

    <template v-else>
      <FilterBar v-model:search="q" :placeholder="t.actions.searchActions" label="Filter actions" class="mb-5">
        <template #end><PageTabs v-model="view" :items="VIEWS" variant="segmented" label="View" id-prefix="actions-view" /></template>
      </FilterBar>

      <!-- By target -->
      <div v-if="view === 'target'" class="grid gap-6 lg:grid-cols-[17rem_minmax(0,1fr)]">
        <aside class="flex flex-col gap-3 lg:sticky lg:top-20 lg:self-start" aria-label="Targets">
          <SearchInput v-model="targetQ" :placeholder="t.actions.searchTargets" />
          <nav class="surface flex max-h-[60vh] flex-col gap-1 overflow-y-auto p-2 lg:max-h-[calc(100vh-12rem)]">
            <p v-if="groups.length === 0" class="px-2 py-4 text-center text-sm text-muted-foreground">{{ t.common.noResults }}</p>
            <div v-for="g in groups" :key="g.lineage" class="rounded-lg px-2 py-1.5">
              <p class="mb-1.5 truncate text-sm font-medium">{{ g.lineage }}</p>
              <div class="flex flex-wrap gap-1.5">
                <TargetChip
                  v-for="tg in g.targets"
                  :key="tg.id"
                  :target="tg.id"
                  env-only
                  :active="tg.id === target"
                  :to="`/actions?target=${encodeURIComponent(tg.id)}`"
                />
              </div>
            </div>
          </nav>
        </aside>

        <section class="min-w-0" :aria-label="selected ? t.actions.actionsOn(onTarget.length, selected.id) : t.actions.chooseTarget">
          <EmptyState v-if="!selected" compact :icon="Server" :title="t.actions.chooseTarget" :description="t.actions.chooseTargetHint" class="lg:py-16" />
          <template v-else>
            <div
              :class="
                cn(
                  'surface mb-4 flex flex-wrap items-center gap-3 p-4',
                  isProd(selected.id) && 'border-destructive/30 bg-destructive-soft/50',
                )
              "
            >
              <div :class="cn('grid size-10 place-items-center rounded-lg', isProd(selected.id) ? 'bg-destructive text-white' : 'bg-primary/10 text-primary')" aria-hidden="true">
                <Server class="size-5" />
              </div>
              <div class="min-w-0 flex-1">
                <p class="eyebrow">{{ selected.lineage }}</p>
                <h2 class="flex flex-wrap items-center gap-2 text-lg font-semibold">
                  {{ selected.environment }}
                  <span v-if="isProd(selected.id)" class="rounded-md bg-destructive px-1.5 py-0.5 text-[11px] font-semibold tracking-wide text-white uppercase">{{ t.actions.production }}</span>
                </h2>
              </div>
              <p class="basis-full text-sm text-muted-foreground sm:basis-auto">{{ t.actions.actionsOn(onTarget.length, selected.id) }}</p>
            </div>
            <EmptyState v-if="onTarget.length === 0" compact :icon="q ? SearchX : Zap" :title="q ? t.common.noResults : t.actions.noActionsHere" :description="q ? undefined : t.actions.noTargetsHint" />
            <ul v-else class="grid grid-cols-[minmax(0,1fr)] gap-3 xl:grid-cols-2">
              <li v-for="a in onTarget" :key="a.name" class="flex min-w-0">
                <ActionCard class="w-full" :action="a" :target="selected.id" :last-run="lastRunOf(a.name)" @run="openRun" />
              </li>
            </ul>
          </template>
        </section>
      </div>

      <!-- All actions -->
      <template v-else>
        <EmptyState v-if="filteredAll.length === 0" :icon="SearchX" :title="t.common.noResults" />
        <ul v-else class="grid grid-cols-[minmax(0,1fr)] gap-3 md:grid-cols-2 xl:grid-cols-3 min-[1800px]:grid-cols-4">
          <li v-for="a in filteredAll" :key="a.name" class="flex min-w-0">
            <ActionCard class="w-full" :action="a" @run="openRun" />
          </li>
        </ul>
      </template>
    </template>

    <Sheet v-model:open="sheetOpen">
      <SheetContent side="right" class="w-full gap-0 overflow-y-auto sm:max-w-xl">
        <SheetHeader>
          <SheetTitle class="flex items-center gap-2 pr-8"><Play class="size-4" aria-hidden="true" /> {{ sheetAction?.title }}</SheetTitle>
          <SheetDescription>{{ sheetAction?.description }}</SheetDescription>
        </SheetHeader>
        <div v-if="sheetAction" class="flex flex-1 flex-col px-4">
          <RunActionForm :action="sheetAction" :target="view === 'target' ? target : undefined" id-prefix="sheet" in-sheet @started="sheetOpen = false" />
        </div>
      </SheetContent>
    </Sheet>
  </div>
</template>
